import { all, call, fork, put, select, take } from "redux-saga/effects";

// Actions
import * as actionsCreator from "./reducer";

// Lib
import { RootState } from "../../store";
import { convertDataPrices } from "lib/helpers";

// Constants
import { mainActivity } from "pages/MoleculeProduct/reducer";

// Saga helper
import {
  getCompoundByIdRequest,
  getMainActivityRequest,
  getChemicalProbesByIdRequest,
  getCompoundsInactiveControlByIdRequest,
} from "./saga-helper";

export const auth = (store: RootState) => store.settings;
export const moleculeProductState = (store: RootState) => store.moleculeProduct;

function* _select<T>(fn: (state: RootState) => T) {
  const res: T = yield select(fn);
  return res;
}

export function* moleculeProductStart(api: any, action: any) {
  yield fork(getMoleculeProductWatcher, api, action);
  yield fork(getSolubilitiesByEbcIdWatcher, api, action);

  yield fork(getConvertAnyIdToEbcIdWatcher, api, action);
  yield fork(getRelatedCompoundsWatcher, api, action);
}

/**
 *  Check id product watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getConvertAnyIdToEbcIdWatcher(api: any, action: any): Generator<any, void, any> {
  while(true) {
    try {
      const action = yield take(actionsCreator.getConvertAnyIdToEbcId.type);
      const id = action.payload;

      if (id.startsWith('EBC')) {
        yield put(actionsCreator.getMoleculeById(id));
        yield put(actionsCreator.getRelatedCompounds(id));
        yield put(actionsCreator.getSolubilitiesByEbcId(id));
      } else {
        const res = yield call(api.request, "ebcIdByAny", [id]);

        if (res?.ok) {
          const ebcId = res.data[0];
          yield put(actionsCreator.getMoleculeById(ebcId));
          yield put(actionsCreator.getRelatedCompounds(ebcId));
          yield put(actionsCreator.getSolubilitiesByEbcId(ebcId));
        } else {
          yield put(actionsCreator.getConvertAnyIdToEbcIdError());
        }
      }
    } catch (e) {
      console.log("CATCH ERROR: getConvertAnyIdToEbcIdWatcher: ", e);
    }
  }
}

/**
 *  Get related compounds watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getRelatedCompoundsWatcher(api: any, action: any): Generator<any, void, any> {
  while(true) {
    try {
      const actions = yield take(actionsCreator.getRelatedCompounds.type);

      const res = yield call(api.request, "ebcCatalogRelatedCompounds", actions.payload);

      if (!res.ok) {
        yield put(actionsCreator.getRelatedCompoundsError());
      } else {
        yield put(actionsCreator.getRelatedCompoundsSuccess(res.data || []));
      }
    } catch (e) {
      console.log("CATCH ERROR: getRelatedCompoundsWatcher: ", e);
    }
  }
}

/**
 *  Get molecule product watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getMoleculeProductWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      const actions = yield take(actionsCreator.getMoleculeById.type);
      const state = yield* _select(auth);
      const moleculeInfo = yield _select(moleculeProductState);
      const authorized = state.user.authorized;
      const typePrice = state.typePrice;

      const id = actions.payload;

      const requests = [
        call(getCompoundByIdRequest, api, id),
        call(getChemicalProbesByIdRequest, api, id),
        call(getCompoundsInactiveControlByIdRequest, api, id),
      ];

      if (authorized) {
        requests.push(call(getMainActivityRequest, api, id));
      }

      const results = yield all(requests);
      const resGetCompoundById = results[0];
      const resChemicalProbes = results[1];
      const resCompoundsInactiveControl = results[2];
      const resGetMainActivity = authorized ? results[3] : { ok: true, data: mainActivity };

      if (
        resGetCompoundById.ok &&
        resChemicalProbes.ok &&
        resCompoundsInactiveControl.ok &&
        (authorized ? resGetMainActivity.ok : true)
      ) {
        const findCurrentPrice = resGetCompoundById.data.prices.find(
          (item: any) => item.supplier === typePrice
        );
        const convertData = convertDataPrices(findCurrentPrice.prices || []);
        const newData = {
          data: {
            ...moleculeInfo.data,
            ...resGetCompoundById.data,
            prices: resGetCompoundById.data.prices,
          },
          mainActivity: resGetMainActivity.data,
          chemicalProbes: resChemicalProbes?.data,
          inactiveControl: resCompoundsInactiveControl.data,
          disabled: !convertData.filter((x: { [index: string]: string | boolean }) => x.available).length
        };
        yield put(actionsCreator.getMoleculeByIdSuccess(newData));
      } else {
        yield put(actionsCreator.getMoleculeByIdError());
      }
    } catch (e) {
      console.log("CATCH ERROR: getMoleculeProductWatcher: ", e);
      yield put(actionsCreator.getMoleculeByIdError());
    }
  }
}

/**
 *  Get list solubilities by Ebc id watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getSolubilitiesByEbcIdWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      const actions = yield take(actionsCreator.getSolubilitiesByEbcId.type);

      const res = yield call(api.request, "getSolubilitiesByEbcId", actions.payload);

      if (!res.ok) {
        yield put(actionsCreator.getSolubilitiesByEbcIdError());
      } else {
        yield put(actionsCreator.getSolubilitiesByEbcIdSuccess(res.data || []));
      }
    } catch (e) {
      console.log("CATCH ERROR: getSolubilitiesByEbcIdWatcher: ", e);
    }
  }
}

